import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { DragSource, DropTarget } from 'react-dnd';
import { findDOMNode } from 'react-dom';
import flow from 'lodash/flow';
import Toggle from 'react-toggle';
import { Dropdown } from './Dropdown';

const cardSource = {
  beginDrag(props) {
    return {
      id: props.id,
      index: props.index,
    };
  },
};

const cardTarget = {
  hover(props, monitor, component) {
    const dragIndex = monitor.getItem().index;
    const hoverIndex = props.index;

    // Don't replace items with themselves, and don't allow resorting when editing
    if (dragIndex === hoverIndex || props.isEditingActive) {
      return;
    }

    // Determine rectangle on screen
    const hoverBoundingRect = findDOMNode(component).getBoundingClientRect();

    // Get vertical middle
    const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

    // Determine mouse position
    const clientOffset = monitor.getClientOffset();

    // Get pixels to the top
    const hoverClientY = clientOffset.y - hoverBoundingRect.top;

    // Only perform the move when the mouse has crossed half of the items height
    // When dragging downwards, only move when the cursor is below 50%
    // When dragging upwards, only move when the cursor is above 50%

    // Dragging downwards
    if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
      return;
    }

    // Dragging upwards
    if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
      return;
    }

    // Time to actually perform the action
    props.moveCard(dragIndex, hoverIndex);

    // Note: we're mutating the monitor item here!
    // Generally it's better to avoid mutations,
    // but it's good here for the sake of performance
    // to avoid expensive index searches.
    monitor.getItem().index = hoverIndex;
  },
};

class SortableListGroupItemComponent extends Component {
  static propTypes = {
    connectDragSource: PropTypes.func.isRequired,
    connectDropTarget: PropTypes.func.isRequired,
    index: PropTypes.number.isRequired,
    isDragging: PropTypes.bool.isRequired,
    id: PropTypes.any.isRequired,
    heading: PropTypes.string.isRequired,
    details: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    onRemove: PropTypes.func,
    onEditClicked: PropTypes.func,
    isEditingActive: PropTypes.bool,
    moveCard: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    onDisableClicked: PropTypes.func,
    disableAction: PropTypes.string,
    imageUrl: PropTypes.string,
    onSelectedClicked: PropTypes.func,
    selectedIdOptions: PropTypes.array,
    selectedId: PropTypes.string,
    labelBadge: PropTypes.string,
  };

  render() {
    const {
      heading,
      details,
      isDragging,
      connectDragSource,
      connectDropTarget,
      onRemove,
      onEditClicked,
      isEditingActive,
      disabled,
      onDisableClicked,
      disableAction,
      imageUrl,
      onSelectedClicked,
      selectedIdOptions,
      selectedId,
      labelBadge,
    } = this.props;
    const opacity = isDragging ? 0 : 1;

    return connectDragSource(
      connectDropTarget(
        <li
          className="list-group-item align-items-start p-2"
          style={{ opacity }}
        >
          <div className="row">
            {imageUrl && (
              <div className="col-sm-1">
                <img src={imageUrl} alt={heading} className="img-fluid" />
              </div>
            )}
            <div className="col-5">
              <div className="d-flex pull-right w-100 justify-content-between align-items-center">
                <div className="font-weight-bold">{heading}</div>
              </div>
            </div>
            {!isEditingActive && (
              <div className="col-4">
                {onDisableClicked && (
                  <Fragment>
                    <span className="me-3">{disableAction}</span>
                    <Toggle
                      className="align-middle me-3"
                      checked={disabled}
                      onChange={(e) => onDisableClicked(e.target.checked)}
                    />
                  </Fragment>
                )}
                {onSelectedClicked && (
                  <Dropdown
                    className="align-middle"
                    items={selectedIdOptions}
                    selectedId={selectedId}
                    onChange={(selectedId) => onSelectedClicked(selectedId)}
                  />
                )}
              </div>
            )}
            <div className="col-3">
              <div className="pull-right">
                {labelBadge && (
                  <span className="badge text-bg-warning mt-2">
                    {labelBadge}
                  </span>
                )}
                {!isEditingActive && onEditClicked && (
                  <button
                    type="button"
                    className="btn btn-link pe-2"
                    onClick={onEditClicked}
                  >
                    edit
                  </button>
                )}
                {!isEditingActive && onRemove && (
                  <button
                    type="button"
                    className="btn btn-link p-0"
                    onClick={onRemove}
                  >
                    remove
                  </button>
                )}
              </div>
            </div>
          </div>
          {details && (
            <div className="row">
              <div className="col">
                <div className="d-flex w-100 align-items-center mt-1"></div>
                {details}
              </div>
            </div>
          )}
        </li>
      )
    );
  }
}

const SortableListGroupItem = flow([
  DragSource('sortableListGroupItem', cardSource, (connect, monitor) => ({
    connectDragSource: connect.dragSource(),
    isDragging: monitor.isDragging(),
  })),
  DropTarget('sortableListGroupItem', cardTarget, (connect) => ({
    connectDropTarget: connect.dropTarget(),
  })),
])(SortableListGroupItemComponent);
export { SortableListGroupItem };
